<template>
    <div class="fp-wrap">
        <div class="fp-backdrop" @click="$emit('fermer')"></div>

        <section class="fp-sheet" role="dialog" aria-modal="true" :aria-label="item.libelle">
            <!-- ============ VISUEL ============ -->
            <div class="fp-hero">
                <img v-if="item.image" :src="item.image" :alt="item.libelle" class="fp-hero-img">
                <div v-else class="fp-hero-vide"><i class="ri-restaurant-line"></i></div>
                <div class="fp-hero-voile"></div>

                <span class="fp-handle"></span>
                <button class="fp-close" aria-label="Fermer" @click="$emit('fermer')">
                    <i class="ri-close-line"></i>
                </button>

                <span v-if="platDuJour(item)" class="fp-hero-tag">Plat du jour</span>
            </div>

            <!-- ============ CONTENU DÉFILANT ============ -->
            <div class="fp-scroll">
                <div class="fp-head">
                    <h1 class="fp-title">{{ item.libelle }}</h1>
                    <p v-if="item.description" class="fp-desc">{{ item.description }}</p>
                </div>

                <!-- Portions : choix exclusif, obligatoire -->
                <section v-if="lesTarifs.length > 1" class="fp-bloc">
                    <div class="fp-bloc-head">
                        <h2>Choisissez votre portion</h2>
                        <span class="fp-req">Obligatoire</span>
                    </div>
                    <div class="fp-portions">
                        <button v-for="t in lesTarifs" :key="t.pid"
                                class="fp-portion" :class="{ active: tarifPid === t.pid }"
                                @click="tarifPid = t.pid">
                            <!-- Le libellé porte déjà le montant (« Portion de
                                 2 500 FCFA ») : réafficher le prix à côté
                                 ferait doublon. Le prix barré reste utile si
                                 une promo est un jour remontée. -->
                            <span class="fp-portion-txt">
                                <span class="fp-portion-nom">{{ t.libelle }}</span>
                                <span v-if="enPromo(t)" class="fp-portion-prix">
                                    <span class="fp-portion-barre">{{ fmt(t.pu) }}</span>
                                </span>
                            </span>
                            <span class="fp-radio"><span class="fp-radio-dot"></span></span>
                        </button>
                    </div>
                </section>

                <!-- Accompagnements : simple ou multiple selon l'article -->
                <section v-if="lesAccomp.length" class="fp-bloc">
                    <div class="fp-bloc-head">
                        <h2>Accompagnement</h2>
                        <span class="fp-req">{{ multi ? 'Plusieurs possibles' : 'Un seul' }}</span>
                    </div>
                    <div class="fp-chips">
                        <button v-for="a in lesAccomp" :key="a.pid"
                                class="fp-chip" :class="{ active: estChoisi(a) }"
                                @click="basculer(a)">
                            {{ a.name }}
                            <span v-if="Number(a.supplement) > 0" class="fp-chip-sup">
                                + {{ Number(a.supplement).toLocaleString('fr-FR') }}
                            </span>
                        </button>
                    </div>
                </section>

                <!-- Note cuisine -->
                <section class="fp-bloc">
                    <div class="fp-bloc-head"><h2>Un mot pour la cuisine ?</h2></div>
                    <textarea v-model="note" rows="3" maxlength="180"
                              placeholder="bien cuit, sans piment…"></textarea>
                </section>
            </div>

            <!-- ============ BARRE D'ACTION PERSISTANTE ============ -->
            <div class="fp-cta">
                <div class="fp-cta-top">
                    <div class="fp-stepper">
                        <button aria-label="Retirer" @click="qte = Math.max(1, qte - 1)">
                            <i class="ri-subtract-line"></i>
                        </button>
                        <span>{{ qte }}</span>
                        <button aria-label="Ajouter" @click="qte = Math.min(30, qte + 1)">
                            <i class="ri-add-line"></i>
                        </button>
                    </div>
                    <div class="fp-cta-total">
                        <span class="fp-cta-total-lbl">Total</span>
                        <span class="fp-cta-total-val">{{ fmt(total) }}</span>
                    </div>
                </div>

                <button class="fp-btn" :disabled="!valide" @click="valider">
                    <span>Ajouter à ma commande</span>
                    <span class="fp-btn-sep"></span>
                    <span class="fp-btn-prix">{{ fmt(total) }}</span>
                </button>
                <p v-if="!valide" class="fp-err">{{ messageManquant }}</p>
            </div>
        </section>
    </div>
</template>

<script>
    /* eslint-disable */
    import {
        fmt, tarifs, tarifDefaut, prixTarif, enPromo, platDuJour, multiAccomp,
    } from "./format";

    export default {
        name: "ficheplat",
        props: {
            item: { type: Object, required: true },
        },
        emits: ["fermer", "confirmer"],
        data: () => ({ tarifPid: "", accomp: [], note: "", qte: 1 }),
        computed: {
            lesTarifs() { return tarifs(this.item); },
            lesAccomp() { return this.item.accompagnement || []; },
            multi() { return multiAccomp(this.item); },
            tarifChoisi() {
                return this.lesTarifs.find(t => t.pid === this.tarifPid) || null;
            },
            supplements() {
                return this.accomp.reduce((s, nom) => {
                    const a = this.lesAccomp.find(x => x.name === nom);
                    return s + (a ? Number(a.supplement) || 0 : 0);
                }, 0);
            },
            prixUnitaire() {
                return (this.tarifChoisi ? prixTarif(this.tarifChoisi) : 0) + this.supplements;
            },
            total() { return this.prixUnitaire * this.qte; },
            // Un accompagnement n'est obligatoire que si l'article en propose.
            valide() {
                if (!this.tarifChoisi) return false;
                if (this.lesAccomp.length && !this.accomp.length) return false;
                return true;
            },
            messageManquant() {
                if (!this.tarifChoisi) return "Choisissez une portion pour continuer.";
                return "Choisissez un accompagnement pour continuer.";
            },
        },
        created() {
            const t = tarifDefaut(this.item);
            this.tarifPid = t ? t.pid : "";
            // Choix simple : on présélectionne le premier, le client n'a rien à faire
            // s'il prend le choix par défaut. Choix multiple : on ne présume rien.
            if (this.lesAccomp.length && !this.multi) this.accomp = [this.lesAccomp[0].name];
            document.body.style.overflow = "hidden";
        },
        beforeUnmount() { document.body.style.overflow = ""; },
        methods: {
            fmt, prixTarif, enPromo, platDuJour,
            estChoisi(a) { return this.accomp.indexOf(a.name) !== -1; },
            basculer(a) {
                if (!this.multi) { this.accomp = [a.name]; return; }
                const i = this.accomp.indexOf(a.name);
                if (i === -1) this.accomp.push(a.name);
                else this.accomp.splice(i, 1);
            },
            valider() {
                if (!this.valide) return;
                this.$emit("confirmer", {
                    item: this.item,
                    tarif: this.tarifChoisi,
                    accompagnements: this.accomp.slice(),
                    note: this.note.trim(),
                    qte: this.qte,
                    prixUnitaire: this.prixUnitaire,
                });
            },
        },
    };
</script>

<style scoped>
    /* eslint-disable */
    .fp-wrap {
        position: fixed; inset: 0; z-index: 200;
        display: flex; flex-direction: column; justify-content: flex-end;
    }
    .fp-backdrop {
        position: absolute; inset: 0;
        background: rgba(42, 23, 19, 0.5); backdrop-filter: blur(3px);
        animation: fp-fade 0.2s ease;
    }
    @keyframes fp-fade { from { opacity: 0; } to { opacity: 1; } }

    .fp-sheet {
        position: relative; display: flex; flex-direction: column;
        width: 100%; max-height: 94vh; background: var(--c-surface);
        border-radius: 24px 24px 0 0; overflow: hidden;
        box-shadow: 0 -8px 30px rgba(42, 23, 19, 0.25);
        animation: fp-up 0.3s cubic-bezier(0.2, 0.9, 0.25, 1);
    }
    @keyframes fp-up { from { transform: translateY(100%); } to { transform: translateY(0); } }

    /* ---------- Visuel ---------- */
    .fp-hero { position: relative; height: 240px; flex-shrink: 0; background: var(--c-surface-container); }
    .fp-hero-img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fp-hero-vide {
        width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
        font-size: 4rem; color: var(--c-outline-variant);
        background: linear-gradient(135deg, var(--c-surface-container), var(--c-surface-high));
    }
    .fp-hero-voile {
        position: absolute; inset: 0;
        background: linear-gradient(to bottom, rgba(42,23,19,0.35), transparent 45%);
        pointer-events: none;
    }
    .fp-handle {
        position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
        width: 48px; height: 5px; border-radius: 999px; background: rgba(255, 255, 255, 0.55);
    }
    .fp-close {
        position: absolute; top: 14px; right: 14px; width: 40px; height: 40px;
        border-radius: 999px; cursor: pointer; font-size: 1.25rem; color: #fff;
        background: rgba(255, 255, 255, 0.2); border: 1px solid rgba(255, 255, 255, 0.35);
        backdrop-filter: blur(8px);
        display: flex; align-items: center; justify-content: center;
        transition: transform 0.12s;
    }
    .fp-close:active { transform: scale(0.9); }
    .fp-hero-tag {
        position: absolute; left: var(--sp-pad); bottom: var(--sp-md);
        background: var(--c-primary); color: #fff;
        font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em;
        padding: 5px 12px; border-radius: 999px;
    }

    /* ---------- Contenu ---------- */
    .fp-scroll {
        flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch;
        padding: var(--sp-lg) var(--sp-pad) var(--sp-xl);
    }
    .fp-head { margin-bottom: var(--sp-xl); }
    .fp-title {
        margin: 0 0 var(--sp-xs); font-size: 30px; line-height: 36px;
        font-weight: 800; letter-spacing: -0.02em; color: var(--c-on-surface);
    }
    .fp-desc { margin: 0; font-size: 16px; line-height: 24px; color: var(--c-on-surface-variant); }

    .fp-bloc { margin-bottom: var(--sp-xl); }
    .fp-bloc-head {
        display: flex; align-items: center; justify-content: space-between; gap: var(--sp-xs);
        margin-bottom: var(--sp-md);
    }
    .fp-bloc-head h2 {
        margin: 0; font-size: 18px; line-height: 24px; font-weight: 700; color: var(--c-on-surface);
    }
    .fp-req {
        flex-shrink: 0; font-size: 11px; font-weight: 800;
        text-transform: uppercase; letter-spacing: 0.05em;
        color: var(--c-primary); background: var(--c-surface-container);
        padding: 4px 10px; border-radius: 999px;
    }

    /* ---------- Portions ---------- */
    .fp-portions { display: flex; flex-direction: column; gap: var(--sp-sm); }
    .fp-portion {
        display: flex; align-items: center; justify-content: space-between; gap: var(--sp-sm);
        width: 100%; text-align: left; cursor: pointer; font-family: inherit;
        padding: var(--sp-md); border-radius: 16px;
        border: 2px solid var(--c-outline-variant); background: var(--c-surface);
        transition: all 0.18s;
    }
    .fp-portion.active { border-color: var(--c-primary); background: var(--c-surface-container); }
    .fp-portion:active { transform: scale(0.99); }
    .fp-portion-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .fp-portion-nom { font-size: 16px; font-weight: 600; color: var(--c-on-surface); }
    .fp-portion-prix {
        font-size: 18px; font-weight: 700; color: var(--c-primary);
        font-variant-numeric: tabular-nums;
    }
    .fp-portion-barre {
        margin-left: 6px; font-size: 13px; font-weight: 500;
        color: var(--c-on-surface-variant); text-decoration: line-through;
    }
    .fp-radio {
        flex-shrink: 0; width: 26px; height: 26px; border-radius: 999px;
        border: 2px solid var(--c-outline-variant);
        display: flex; align-items: center; justify-content: center;
        transition: all 0.18s;
    }
    .fp-portion.active .fp-radio { border-color: var(--c-primary); background: var(--c-primary); }
    .fp-radio-dot {
        width: 9px; height: 9px; border-radius: 999px; background: #fff;
        opacity: 0; transform: scale(0.4); transition: all 0.18s;
    }
    .fp-portion.active .fp-radio-dot { opacity: 1; transform: scale(1); }

    /* ---------- Accompagnements ---------- */
    .fp-chips { display: flex; flex-wrap: wrap; gap: var(--sp-sm); }
    .fp-chip {
        cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 600;
        color: var(--c-on-surface); background: var(--c-surface-low);
        border: 2px solid transparent; border-radius: 999px;
        padding: 12px 18px; min-height: 46px;
        transition: all 0.15s;
    }
    .fp-chip:active { transform: scale(0.95); }
    .fp-chip.active {
        border-color: var(--c-primary); background: var(--c-surface-container); font-weight: 700;
    }
    .fp-chip-sup { margin-left: 6px; font-weight: 800; color: var(--c-on-surface-variant); }
    .fp-chip.active .fp-chip-sup { color: var(--c-primary); }

    /* ---------- Note ---------- */
    .fp-bloc textarea {
        width: 100%; resize: none; font-family: inherit; font-size: 16px; line-height: 24px;
        color: var(--c-on-surface); background: var(--c-surface-low);
        border: 1px solid var(--c-outline-variant); border-radius: 16px; padding: var(--sp-md);
    }
    .fp-bloc textarea::placeholder { color: var(--c-outline); }
    .fp-bloc textarea:focus { outline: 3px solid rgba(255, 48, 8, 0.18); border-color: transparent; }

    /* ---------- Barre d'action ---------- */
    .fp-cta {
        flex-shrink: 0; border-top: 1px solid var(--c-outline-variant);
        background: var(--c-surface);
        background: color-mix(in srgb, var(--c-surface) 94%, transparent);
        backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
        padding: var(--sp-md) var(--sp-pad) calc(var(--sp-lg) + env(safe-area-inset-bottom, 0px));
    }
    .fp-cta-top {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: var(--sp-md);
    }
    .fp-stepper {
        display: flex; align-items: center; gap: 4px; padding: 4px;
        background: var(--c-surface-low); border: 1px solid var(--c-outline-variant);
        border-radius: 999px;
    }
    .fp-stepper button {
        width: 42px; height: 42px; border: none; border-radius: 999px; cursor: pointer;
        background: var(--c-surface); color: var(--c-primary); font-size: 1.15rem;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 1px 3px rgba(42, 23, 19, 0.12); transition: transform 0.1s;
    }
    .fp-stepper button:active { transform: scale(0.9); }
    .fp-stepper > span {
        min-width: 34px; text-align: center; font-size: 18px; font-weight: 700;
        font-variant-numeric: tabular-nums; color: var(--c-on-surface);
    }
    .fp-cta-total { text-align: right; }
    .fp-cta-total-lbl {
        display: block; font-size: 12px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.06em; color: var(--c-on-surface-variant);
    }
    .fp-cta-total-val {
        display: block; font-size: 20px; font-weight: 800; color: var(--c-on-surface);
        font-variant-numeric: tabular-nums;
    }

    .fp-btn {
        width: 100%; height: 56px; border: none; border-radius: 999px; cursor: pointer;
        background: var(--c-primary); color: #fff; font-family: inherit;
        font-size: 16px; font-weight: 700;
        display: flex; align-items: center; justify-content: center; gap: var(--sp-sm);
        box-shadow: 0 6px 18px rgba(255, 48, 8, 0.3);
        transition: transform 0.12s, opacity 0.15s;
    }
    .fp-btn:active { transform: scale(0.98); }
    .fp-btn:disabled { opacity: 0.45; box-shadow: none; cursor: not-allowed; }
    .fp-btn-sep { width: 1px; height: 18px; background: rgba(255, 255, 255, 0.35); }
    .fp-btn-prix { font-variant-numeric: tabular-nums; }
    .fp-err {
        margin: var(--sp-sm) 0 0; text-align: center;
        font-size: 13px; font-weight: 600; color: var(--c-error);
    }
</style>
